import { booleanAttribute, ChangeDetectionStrategy, Component, computed, effect, inject, input, linkedSignal, model, signal, viewChild, ViewEncapsulation, WritableSignal } from '@angular/core';
import { Visual } from '../Visual';
import { VisualStyle } from '../VisualStyle';
import { VisualComponent } from './Visual.component';
import { debounceTime, distinctUntilChanged, Subject, tap } from 'rxjs';
import { ExprEditor, ExprEditorComponent, ExprEditorProvider } from './ExprEditor.component';
import { logger } from '../../utils/logger';
import { debounceSignal } from '../../utils/signal';
import { DataType } from '../../dataModel/DataType';

const LOGGER = logger('ExprField');

@Component({
  selector: 'app-expr-field',
  template: `
    <input type="text"
           class="trg-outline expr-field-text-input"
           [required]="required()"
           [disabled]="disabled()"
           [readonly]="true"
           [value]="value()" />
    <div [hidden]="!exprEditor()" class="expr-field-edit-btn" (click)="openEditor($event)">
      <i class="fas fa-edit"></i>
    </div>
    @if (!providedExprEditor()) {
      <app-expr-editor #ownExprEditor
                       [disabled]="disabled()"
                       [readonly]="readonly()"
                       [allowEmpty]="allowEmpty()"
                       [checkForCyclicDeps]="null"
                       [format]="format()"
                       [nameEditing]="false"
                       [formatEditing]="false"
                       [(value)]="value"></app-expr-editor>
    }
  `,
  styles: [`
    app-expr-field {
      display: inline-block;
      position: relative;

      > .expr-field-text-input {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        padding-right: 20px;
        width: 100%;
      }

      > .expr-field-edit-btn{
        cursor: pointer;
        position: absolute;
        right: 7px;
        top: calc(50% - 7px);
      }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class ExprFieldComponent {
  readonly exprEditorProvider = inject(ExprEditorProvider, { optional: true });
  readonly providedExprEditor = input<ExprEditorComponent>(null, { alias: 'exprEditor' });
  private readonly ownExprEditor = viewChild('ownExprEditor', { read: ExprEditorComponent });
  readonly exprEditor = computed(() => this.providedExprEditor() ?? this.exprEditorProvider.value() ?? this.ownExprEditor());

  readonly required = input(false, { transform: booleanAttribute });
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly readonly = input(false, { transform: booleanAttribute });

  readonly editorDisplayMode = input<ExprEditor.DisplayMode>(ExprEditor.DisplayModes.full);

  readonly allowEmpty = input(true);
  readonly identifier = input<string>(null);
  readonly checkForCyclicDeps = input<boolean>(true);
  readonly format = input<ExprEditor.Format>(null);

  readonly value = model<string>('');
  
  readonly valid = model(true);
  readonly issues = model<ReadonlyArray<ExprEditor.Issue>>([]);
  readonly anyWarnings = model(false);
  readonly anyErrors = model(false);

  private readonly valueToValidate = debounceSignal(this.value, 100);

  private readonly validateValueOnChange = effect(() => {
    let identifier = this.identifier();
    let exprEditor = this.exprEditor();
    if (!exprEditor)
      return;

    let value = this.valueToValidate();

    let validationResult = exprEditor.validateExpr({
      expr: value,
      allowEmpty: this.allowEmpty(),
      checkForCyclicDeps: (this.checkForCyclicDeps() && identifier) ? { identifier } : null,
    });

    LOGGER.info(`${identifier ?? 'undefined'}: validation result`, {
      field: this,
      expr: value,
      identifier,
      ...validationResult,
    });

    this.valid.set(validationResult.valid);
    this.issues.set(validationResult.issues);
    this.anyWarnings.set(validationResult.anyWarnings);
    this.anyErrors.set(validationResult.anyErrors);
  });


  openEditor(e: Event) {
    e.stopPropagation();

    if (this.readonly() || this.disabled())
      return;

    this.exprEditor().open({
      expr: this.value(),
      allowEmpty: this.allowEmpty(),
      displayMode: this.editorDisplayMode(),
      nameEditing: false,
      formatEditing: false,
    });
  }
}
